import { useMemo } from "react"

// ** Third Party Components
import axiosServices from "@pma/utils/axios"
import { useQuery } from "@tanstack/react-query"

// ** API Routes
import { PROJECTS_BY_CURRENT_USER_FILTER } from "@pma/utils/api-routes"

// ** Services
import { getWorkDiaryListService } from "@pma/services/task"

// ** Type
import { AnalyticsFilters } from "@pma/types"

export function useDiaryQueries(orgId: number | undefined, filters?: AnalyticsFilters) {
  const {
    data: projectsRaw,
    isLoading: projectsLoading,
    refetch: refetchProjects,
  } = useQuery<any[]>({
    queryKey: ["diary-projects", orgId],
    queryFn: async () => {
      const res = await axiosServices.post(PROJECTS_BY_CURRENT_USER_FILTER, {
        paging_start_page: 1,
        paging_per_page: 100,
        by_organization_id: orgId,
      })
      return res.data?.items || res.data || []
    },
    enabled: !!orgId,
    staleTime: 60_000,
  })

  const projects = useMemo(() => {
    if (!projectsRaw?.length) return []
    if (filters?.projectIds?.length) {
      return projectsRaw.filter((p: any) => filters.projectIds.includes(p.id))
    }
    return projectsRaw
  }, [projectsRaw, filters?.projectIds])

  const projectIdKey = projects.map((p: any) => p.id).join(",")

  const {
    data: allDiaryData,
    isLoading: diariesLoading,
  } = useQuery<{ project: any; diaries: any[] }[]>({
    queryKey: ["diary-entries", orgId, projectIdKey],
    queryFn: async () => {
      if (!projects.length) return []
      const results = await Promise.allSettled(
        projects.map(async (project: any) => {
          const diaries = await getWorkDiaryListService({
            project_id: project.id,
            organization_id: orgId,
          })
          return { project, diaries: Array.isArray(diaries) ? diaries : [] }
        }),
      )
      const collected: { project: any; diaries: any[] }[] = []
      results.forEach((r) => {
        if (r.status === "fulfilled") collected.push(r.value)
      })
      return collected
    },
    enabled: !!orgId && projects.length > 0,
    staleTime: 60_000,
  })

  const isLoading = projectsLoading || diariesLoading

  return {
    projects,
    allDiaryData,
    isLoading,
    refetchProjects,
  }
}
